import mixpanel, { Mixpanel } from 'mixpanel-browser'
import { EventName, FeatureName } from '../types'
import { canSendEvent } from './rateLimiter'
import { getAttributionData } from '../attributionUtils'
import { isFramed } from '../isFramed'

declare const mixpanelKey: string

interface MixpanelWithInstances extends Mixpanel {
  smartasset?: Mixpanel
}

let mixpanelInitialized = false

const nodeEnv = typeof process !== 'undefined' && process.env ? process.env.NODE_ENV : 'production'

const getCookie = (name: string): string | undefined => {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop()?.split(';').shift()
  return undefined
}

/**
 * The id to identify this browser as: inside an iframe the mixpanelId the
 * server put on the match screen root, then the URL's, otherwise the _sa_uuid
 * cookie. A framed snippet page has no SA session, and browsers block or
 * partition its cookies inconsistently, so the server id is the one identity
 * every browser agrees on there.
 */
export const resolveMixpanelUserId = (): string | undefined => {
  const idFromCookie = getCookie('_sa_uuid')
  if (!isFramed()) return idFromCookie
  // Play lowercases attribute names, so data-mixpanelId arrives as mixpanelid
  const idFromRoot = document.getElementById('match-screen-root')?.dataset.mixpanelid
  const idFromUrl = new URLSearchParams(window.location.search).get('mixpanelId')
  return idFromRoot || idFromUrl || idFromCookie
}

// Use the global mixpanelKey that's injected by webpack DefinePlugin
const MIXPANEL_TOKEN = typeof mixpanelKey !== 'undefined' ? mixpanelKey : undefined

if (MIXPANEL_TOKEN && typeof window !== 'undefined') {
  mixpanel.init(MIXPANEL_TOKEN, {
    debug: nodeEnv === 'development',
    persistence: 'localStorage',
    // FSM-993: record session replays for 100% of match screen loads (we have
    // the allocation for the match screen, unlike the funnel).
    record_sessions_percent: 100,
    // FSM-989: unmask text and images so replays are analyzable. Mixpanel masks
    // all text and blocks img/video/audio by default — clear the text-mask
    // selector (empty disables it) and stop blocking images. The default
    // record_mask_text_class regex still applies, so elements tagged `mp-mask`
    // (the user's first name and appointment phone) stay masked. Inputs remain
    // masked by rrweb's (the recording library mixpanel uses) default, keeping form-entered PII out.
    record_mask_text_selector: '',
    record_block_selector: 'video, audio'
  }, 'smartasset')
  mixpanelInitialized = true

  const userId = resolveMixpanelUserId()

  if (userId) {
    const mixpanelInstance = (mixpanel as MixpanelWithInstances).smartasset || mixpanel
    mixpanelInstance.identify(userId)
  }
} else {
  if (nodeEnv === 'development') {
    console.warn(
      'Mixpanel token (REACT_APP_MIXPANEL_KEY) not found or not in browser environment. Mixpanel tracking will be disabled.'
    )
  }
}

export const trackEvent = (
  featureName: FeatureName,
  eventName: EventName,
  properties: Record<string, string | number | boolean | null | undefined | string[]> = {}
): void => {
  if (!mixpanelInitialized) {
    if (nodeEnv === 'development') {
      console.warn(
                `Mixpanel not initialized. Event "${eventName}" for feature "${featureName}" not tracked.`,
                properties
      )
    }
    return
  }

  if (!canSendEvent(eventName)) {
    if (nodeEnv === 'development') {
      console.warn(`Event "${eventName}" for feature "${featureName}" was rate-limited.`)
    }
    return
  }

  // Get attribution data from local storage (first-touch UTM, referrer, session data)
  const attributionData = getAttributionData()

  const eventProperties = {
    ...properties,
    ...attributionData,
    feature: featureName
  }

  // Use the named instance if available, otherwise fall back to default
  const mixpanelInstance = (mixpanel as MixpanelWithInstances).smartasset || mixpanel
  mixpanelInstance.track(eventName, eventProperties)
}

export const identifyUser = (userId: string) => {
  if (mixpanelInitialized) {
    const mixpanelInstance = (mixpanel as MixpanelWithInstances).smartasset || mixpanel
    mixpanelInstance.identify(userId)
  }
}

export const registerSuperProperties = (
  properties: Record<string, string | number | boolean | null | undefined>,
  options?: { persistent?: boolean }
) => {
  if (mixpanelInitialized) {
    const mixpanelInstance = (mixpanel as MixpanelWithInstances).smartasset || mixpanel
    // register() persists to localStorage by default; pass {persistent: false}
    // for properties that must die with the page (e.g. the preview flag).
    mixpanelInstance.register(properties, options)
  }
}
